import Icon from "../components/Icon";

const values = [
  {
    title: "Mission",
    copy: "Help people find welcoming communities quickly so organizers can focus on members, not marketing.",
    icon: "compass",
    tone: "sun"
  },
  {
    title: "Friendly by design",
    copy: "No loud ads or harsh gradients—just calm storytelling, soft colors, and maps that feel approachable.",
    icon: "map",
    tone: "mint"
  }
];

const standards = [
  {
    title: "Verification",
    copy: "We review leadership, moderation, and recent activity. Ghost towns never ship.",
    icon: "shield",
    tone: "coral"
  },
  {
    title: "Cross-platform view",
    copy: "Discord, Telegram, Reddit, Slack, newsletters, websites—everything shows up inside one guild dossier.",
    icon: "network",
    tone: "primary"
  },
  {
    title: "Map fidelity",
    copy: "Communities include coordinates, cadence, tags, and regional focus so maps stay accurate.",
    icon: "layers",
    tone: "sun"
  },
  {
    title: "Maintenance",
    copy: "Quarterly check-ins confirm activity and governance, backed by a JSON data layer you can extend.",
    icon: "filter",
    tone: "mint"
  }
];

const roadmap = [
  {
    title: "Submission embed",
    detail: "Collect evidence, codes of conduct, and coordinates for map placement.",
    quarter: "Q1",
    tone: "sun"
  },
  {
    title: "Metrics overlay",
    detail: "Optional participation signals layered on guild mini-maps.",
    quarter: "Q2",
    tone: "mint"
  },
  {
    title: "Journeys",
    detail: "Pre-built explorations—AI builders, local clubs, IRL studios.",
    quarter: "Q3",
    tone: "coral"
  }
];

const contactPanels = [
  {
    title: "Curator inbox",
    detail: "hello@memberdiscovery.com",
    note: "Send verification evidence or briefing requests."
  },
  {
    title: "Submission runway",
    detail: "Launch Q1",
    note: "Embed form collects governance docs + coordinates."
  },
  {
    title: "Signals digest",
    detail: "Monthly briefing",
    note: "Opt-in for updates on new guild dossiers."
  }
];

const missionStats = [
  { label: "Guilds verified", value: "34" },
  { label: "Communities tracked", value: "212" },
  { label: "Mini-maps", value: "10" }
];

const practiceCards = [
  {
    title: "Operators first",
    copy: "Verified, ongoing programming keeps every listing accountable to members."
  },
  {
    title: "Spatial storytelling",
    copy: "Maps, coordinates, and cadence markers keep each guild grounded to real activity."
  },
  {
    title: "Calm surfaces",
    copy: "No ads or visual noise—just trusted signals for researchers and stewards."
  }
];

const editorialSteps = [
  {
    title: "Intake",
    detail: "Guild leads share governance docs, moderation policies, and program notes."
  },
  {
    title: "Audit",
    detail: "Curators review activity, map data, and links to confirm consistency."
  },
  {
    title: "Publish",
    detail: "Listings go live with Leaflet-ready coordinates and quarterly check-ins."
  }
];

const alignmentColumns = [
  {
    title: "For research teams",
    body: "Use the atlas to brief executives with platform coverage, safety notes, and program readiness."
  },
  {
    title: "For guild operators",
    body: "Share documentation, cadence, and onboarding artifacts to attract aligned partners."
  },
  {
    title: "For brands & institutions",
    body: "Understand governance, region coverage, and IRL capability before sponsoring or partnering."
  }
];

const evolutionTimeline = [
  { year: "2022", detail: "Initial atlas prototype for three guilds and 40 communities." },
  { year: "2023", detail: "Mini-map pilot with Leaflet, curator workflows, and JSON data pipeline." },
  { year: "2024", detail: "Atlas opens to partner submissions with rolling verification reports." }
];

const About = () => (
  <div className="space-y-10">
    <section className="panel space-y-6">
      <div className="space-y-4">
        <p className="eyebrow">Mission</p>
        <span className="accent-pill text-brand-sky">About Member Discovery</span>
        <h1 className="text-4xl text-slate-100">Friendly discovery for ecosystems that actually ship</h1>
        <p className="text-lg text-slate-200 max-w-3xl">
          Instead of endless lists, we treat guilds like living worlds—clear narrative, map pins, tags, and cadence all in one welcoming surface.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {missionStats.map((stat) => (
          <article key={stat.label} className="experience-strip wow-glow">
            <p className="experience-strip__metric">{stat.value}</p>
            <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">{stat.label}</p>
          </article>
        ))}
      </div>
    </section>

    <div className="section-wrapper" id="contact">
      <section className="space-y-4">
        <div className="section-heading section-heading--left">
          <p className="eyebrow">Practice</p>
          <h2 className="text-3xl text-slate-100">Practice</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {practiceCards.map((item) => (
            <article key={item.title} className="panel space-y-3 text-sm text-slate-300">
              <p className="text-xs uppercase tracking-[0.35em] text-brand-steel">{item.title}</p>
              <p>{item.copy}</p>
            </article>
          ))}
        </div>
      </section>
    </div>

    <div className="section-wrapper">
      <section className="space-y-4">
        <div className="section-heading section-heading--left">
          <p className="eyebrow">Values</p>
          <h2 className="text-3xl text-slate-100">Values</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {values.map((value) => (
            <article key={value.title} className="accent-card space-y-3">
              <span className={`icon-badge icon-badge--${value.tone}`}>
                <Icon name={value.icon} />
              </span>
              <h2 className="text-2xl text-slate-100">{value.title}</h2>
              <p className="text-sm text-slate-300">{value.copy}</p>
            </article>
          ))}
        </div>
      </section>
    </div>

    <div className="section-wrapper" id="contact">
      <section className="space-y-4">
        <div className="section-heading section-heading--left">
          <p className="eyebrow">Standards</p>
          <h2 className="text-3xl text-slate-100">Standards</h2>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          {standards.map((item) => (
            <article key={item.title} className="panel space-y-3">
              <span className={`icon-badge icon-badge--${item.tone}`}>
                <Icon name={item.icon} />
              </span>
              <h3 className="text-xl text-slate-100">{item.title}</h3>
              <p className="text-sm text-slate-300">{item.copy}</p>
            </article>
          ))}
        </div>
      </section>
    </div>

    <div className="section-wrapper">
      <section className="panel space-y-4">
        <div className="section-heading section-heading--left">
          <p className="eyebrow">Process</p>
          <h2 className="text-3xl text-slate-100">Process</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {editorialSteps.map((step) => (
            <article key={step.title} className="timeline-step pl-5 text-sm text-slate-300">
              <p className="text-xs uppercase tracking-[0.35em] text-brand-steel">{step.title}</p>
              <p>{step.detail}</p>
            </article>
          ))}
        </div>
      </section>
    </div>

    <div className="section-wrapper">
      <section className="space-y-4">
        <div className="section-heading section-heading--left">
          <p className="eyebrow">Audience</p>
          <h2 className="text-3xl text-slate-100">Audience</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {alignmentColumns.map((column) => (
            <article key={column.title} className="panel space-y-2 text-sm text-slate-300">
              <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">{column.title}</p>
              <p>{column.body}</p>
            </article>
          ))}
        </div>
      </section>
    </div>

    <div className="section-wrapper">
      <section className="panel space-y-4">
        <div className="section-heading section-heading--left">
          <p className="eyebrow">Roadmap</p>
          <h2 className="text-3xl text-slate-100">Roadmap</h2>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {roadmap.map((item) => (
            <article key={item.title} className="panel space-y-2 text-sm text-slate-300">
              <span className={`icon-badge icon-badge--${item.tone}`}>{item.quarter}</span>
              <h3 className="text-xl text-slate-100">{item.title}</h3>
              <p>{item.detail}</p>
            </article>
          ))}
        </div>
      </section>
    </div>

    <div className="section-wrapper">
      <section className="panel space-y-4">
        <div className="section-heading section-heading--left">
          <p className="eyebrow">Timeline</p>
          <h2 className="text-3xl text-slate-100">Timeline</h2>
        </div>
        <ol className="list-decimal space-y-2 pl-6 text-sm text-slate-300">
          {evolutionTimeline.map((entry) => (
            <li key={entry.year}>
              <span className="font-semibold text-slate-100">{entry.year}:</span> {entry.detail}
            </li>
          ))}
        </ol>
      </section>
    </div>

    <div className="section-wrapper" id="contact">
      <section className="space-y-4">
        <div className="section-heading section-heading--left">
          <p className="eyebrow">Contact</p>
          <h2 className="text-3xl text-slate-100">Contact</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {contactPanels.map((panel) => (
            <article key={panel.title} className="panel space-y-2 text-sm text-slate-300">
              <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">{panel.title}</p>
              <h3 className="text-2xl text-slate-100">{panel.detail}</h3>
              <p>{panel.note}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  </div>
);

export default About;
